<template>
  <div class="sales-notes-component">
    <div class="edit-note__container">
      <div class="textarea-wrapper">
        <textarea
          ref="notesTextarea"
          v-model="editingSalesNotes"
          class="sales-notes__textarea"
          placeholder="Enter sales notes here..."
          rows="8"
        ></textarea>
        <div class="button-row">
          <k-button
            :theme-color="'base'"
            @click="saveSalesNotes"
            :disabled="isSaving || !hasChanges"
            class="save-button"
          >
            <span class="k-icon k-i-flip-vertical"></span>
            {{ isSaving ? 'Saving...' : 'Send' }}
          </k-button>
        </div>
      </div>
    </div>

    <div class="conditions__loader-container" v-if="isLoading">
      <loader :size="'large'" :type="'infinite-spinner'"></loader>
    </div>

    <transition name="dialogfade">
      <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
        <p class="error-dialog__msg">{{ errorMsg }}</p>
      </k-dialog>
    </transition>
  </div>
</template>

<script>
import auth from '../authConfig.js';
import apiService from '../api/api.service.js';

import { Button } from '@progress/kendo-vue-buttons';
import { Loader } from "@progress/kendo-vue-indicators";
import { Dialog } from '@progress/kendo-vue-dialogs';

export default {
  props: ['loanId', 'showSalesNotes'],
  data() {
    return {
      salesNotes: '',
      editingSalesNotes: '',
      isLoading: false,
      isSaving: false,
      showErrorDialog: false,
      errorMsg: '',
      errorTitle: '',
    };
  },
  computed: {
    hasChanges() {
      return this.editingSalesNotes !== this.salesNotes;
    }
  },
  mounted() {
    this.loadSalesNotes();
  },
  watch: {
    showSalesNotes(newVal) {
      if (newVal) {
        this.loadSalesNotes();
      }
    }
  },
  methods: {
    async loadSalesNotes() {
      this.isLoading = true;

      try {
        const tokenResponse = await auth.acquireTokenResponse();

        const response = await apiService.GetLoanFieldList(
          this.loanId,
          ['CX.FPA.SALESNOTES'],
          tokenResponse.accessToken
        );

        if (response?.data?.data && response.data.data.length > 0) {
          this.salesNotes = response.data.data[0].value || '';
          this.editingSalesNotes = this.salesNotes;
        } else {
          this.salesNotes = '';
          this.editingSalesNotes = '';
        }
      } catch (error) {
        console.error('Error loading sales notes:', error);
        this.errorTitle = 'Failed to Load Sales Notes';
        this.errorMsg = error.response?.data?.data || 'Please try again';
        this.toggleErrorDialog();
      } finally {
        this.isLoading = false;
      }
    },
    async saveSalesNotes() {
      this.isSaving = true;

      try {
        const tokenResponse = await auth.acquireTokenResponse();

        const updateField = [{
          id: 'CX.FPA.SALESNOTES',
          value: this.editingSalesNotes
        }];

        await apiService.UpdateLoanFieldValues(
          this.loanId,
          updateField,
          tokenResponse.accessToken
        );

        this.salesNotes = this.editingSalesNotes;
        this.showNotification('Sales notes saved successfully');
      } catch (error) {
        console.error('Error saving sales notes:', error);
        this.errorTitle = 'Failed to Save Sales Notes';
        this.errorMsg = error.response?.data?.data || 'Please try again';
        this.toggleErrorDialog();
      } finally {
        this.isSaving = false;
      }
    },
    showNotification(message) {
      // You can implement a notification system here
      // For now, we'll just log it
      console.log(message);
    },
    toggleErrorDialog() {
      this.showErrorDialog = !this.showErrorDialog;

      if (!this.showErrorDialog) {
        this.errorMsg = '';
        this.errorTitle = '';
      }
    }
  },
  components: {
    'k-dialog': Dialog,
    'k-button': Button,
    'loader': Loader
  }
};
</script>

<style scoped>
.sales-notes-component {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.edit-note__container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  overflow: hidden;
}

.textarea-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
}

.sales-notes__textarea {
  flex: 1;
  width: 100%;
  min-height: 400px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  box-sizing: border-box;
}

.sales-notes__textarea:focus {
  outline: none;
  border-color: var(--accent);
}

.button-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.save-button {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: var(--accent-ltr) !important;
  line-height: 2rem;
  font-weight: 600;
}

.save-button .k-icon {
  font-size: 16px;
}

.conditions__loader-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.dialogfade-enter-active,
.dialogfade-leave-active {
  transition: opacity 0.3s;
}

.dialogfade-enter-from,
.dialogfade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Non-scoped styles for error dialog */
.sales-notes-component .error__dialog .k-window.k-dialog {
  border-radius: 8px;
  overflow: hidden;
  min-width: 30%;
  max-width: 50%;
  min-height: 25%;
  max-height: unset;
  height: unset;
  border: none;
}

.sales-notes-component .error__dialog .k-window-titlebar {
  background-color: var(--error);
  color: var(--on-error);
  padding: 16px;
}

.sales-notes-component .error__dialog .k-dialog-title {
  font-size: 1.8rem;
}

.sales-notes-component .error__dialog .error-dialog__msg {
  margin: 25px;
  text-align: center;
  font-size: 2rem;
}
</style>
